import { useEffect, useMemo, useState } from "react";
import Icon from "./Icon";
import LoadingBlock from "./LoadingBlock";
import EmptyState from "./EmptyState";
import Drawer from "./Drawer";
import { useApi } from "../hooks/useApi";
import {
  listBusinessPaymentMasters,
  attachBusinessPaymentMaster,
  updateBusinessPaymentMaster,
  detachBusinessPaymentMaster,
  listPaymentMasters,
} from "../api/paymentsService";
import { formatINRExact } from "../utils/formatters";
import { paginatedRows } from "../utils/pagination";
import { showApiError, showToast } from "../utils/toast";

const PATTERN_LABEL = {
  one_off: "One-off",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  yearly: "Yearly",
};

/**
 * Payments tab body on the Business detail page. Lists templates attached
 * to this business with per-business amount overrides. The actual billing
 * (instances, receipts) lands in Phase 2/3 — this panel is the data-model
 * surface for now.
 */
export default function BusinessPaymentsPanel({ businessId, onCountChange }) {
  const attached = useApi(
    () => businessId ? listBusinessPaymentMasters(businessId) : Promise.resolve({ data: [] }),
    [businessId],
  );
  const rows = Array.isArray(attached.data?.data) ? attached.data.data : [];

  const [attachOpen, setAttachOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);

  // Report the live attached-template count up so the Payments tab badge
  // stays accurate as the admin attaches / detaches without a page reload.
  useEffect(() => {
    if (!attached.loading) onCountChange?.(rows.length);
  }, [attached.loading, rows.length, onCountChange]);

  const onDetach = async (row) => {
    if (!window.confirm(`Detach "${row.name}" from this business?\n\nFuture bills won't be generated from it. Existing bills (Phase 2+) stay intact.`)) return;
    try {
      await detachBusinessPaymentMaster(businessId, row.id);
      showToast("success", "Template detached");
      attached.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 12, justifyContent: "flex-end" }}>
        <button className="btn btn-primary btn-sm" onClick={() => setAttachOpen(true)}>
          <Icon name="plus" size={13} /> Attach template
        </button>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {attached.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={3} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="dollar-sign"
              title="No payment templates attached"
              hint="Attach a template so bills are auto-generated against this business on its schedule."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Recurrence</th>
                  <th className="t-num">Default</th>
                  <th className="t-num">Effective</th>
                  <th style={{ width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="t-primary fw-600">{r.name}</div>
                      {r.description && (
                        <div className="t-muted text-xs" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 280 }}>
                          {r.description}
                        </div>
                      )}
                    </td>
                    <td><span className="badge badge-neutral">{PATTERN_LABEL[r.recurrence_pattern]}</span></td>
                    <td className="t-num t-muted">{formatINRExact(r.default_amount)}</td>
                    <td className="t-num">
                      <span className={r.amount_override !== null ? "t-primary fw-600" : "t-muted"}>
                        {formatINRExact(r.effective_amount)}
                      </span>
                      {r.amount_override !== null && (
                        <div className="text-xs t-muted">override</div>
                      )}
                    </td>
                    <td>
                      <div className="row gap-sm">
                        <button
                          className="icon-btn"
                          title={r.amount_override !== null ? "Edit override" : "Set override"}
                          onClick={() => setOverrideTarget(r)}
                        >
                          <Icon name="edit" size={13} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Detach"
                          style={{ color: "var(--danger)" }}
                          onClick={() => onDetach(r)}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <AttachPaymentMasterDrawer
        open={attachOpen}
        businessId={businessId}
        alreadyAttached={rows.map((r) => r.id)}
        onClose={() => setAttachOpen(false)}
        onAttached={() => attached.reload()}
      />

      <OverrideDrawer
        row={overrideTarget}
        businessId={businessId}
        onClose={() => setOverrideTarget(null)}
        onSaved={() => {
          setOverrideTarget(null);
          attached.reload();
        }}
      />
    </>
  );
}

const PATTERN_ORDER = ["one_off", "monthly", "quarterly", "half_yearly", "yearly"];

function AttachPaymentMasterDrawer({ open, businessId, alreadyAttached, onClose, onAttached }) {
  const masters = useApi(() => listPaymentMasters({ per_page: 200 }), [], { immediate: false });
  const allRows = paginatedRows(masters.data);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [patternFilter, setPatternFilter] = useState(""); // "" = all recurrences
  const [overrides, setOverrides] = useState({}); // { [masterId]: '1234.50' }
  const [dueDates, setDueDates] = useState({}); // { [masterId]: 'YYYY-MM-DD' }

  useEffect(() => {
    if (open) {
      setSearch("");
      setPatternFilter("");
      setOverrides({});
      setDueDates({});
      masters.reload().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Recurrence options for the filter, limited to patterns actually present.
  const patternOptions = useMemo(() => {
    const present = new Set(allRows.map((m) => m.recurrence_pattern));
    return PATTERN_ORDER.filter((p) => present.has(p));
  }, [allRows]);

  // Filter (recurrence + search) then bucket by recurrence pattern.
  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const buckets = new Map();
    allRows.forEach((m) => {
      if (patternFilter && m.recurrence_pattern !== patternFilter) return;
      if (term && !(m.name || "").toLowerCase().includes(term)) return;
      const key = m.recurrence_pattern || "other";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(m);
    });
    const ordered = PATTERN_ORDER.filter((p) => buckets.has(p));
    const extra = Array.from(buckets.keys()).filter((k) => !PATTERN_ORDER.includes(k));
    return [...ordered, ...extra].map((p) => ({
      key: p,
      name: PATTERN_LABEL[p] || p,
      items: buckets.get(p),
    }));
  }, [allRows, search, patternFilter]);

  // One-off rows default their due date to the template's own date.
  const dueValueFor = (m) =>
    dueDates[m.id] ?? (m.one_off_due_date ? m.one_off_due_date.slice(0, 10) : "");

  const attach = async (m) => {
    setSaving(true);
    try {
      const raw = overrides[m.id];
      const payload = {
        payment_master_id: m.id,
        amount_override: raw !== undefined && raw !== "" ? Number(raw) : null,
      };
      if (m.recurrence_pattern === "one_off") {
        const due = dueValueFor(m);
        if (due) payload.due_date = due;
      }
      const res = await attachBusinessPaymentMaster(businessId, payload);
      showToast("success", res?.message || "Template attached");
      onAttached?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (m) => {
    const attached = alreadyAttached.includes(m.id);
    const isOneOff = m.recurrence_pattern === "one_off";
    return (
      <tr key={m.id}>
        <td className="t-primary">{m.name}</td>
        <td className="t-num">
          {attached ? (
            <span className="t-muted">—</span>
          ) : (
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              style={{ width: 120 }}
              title="Amount override (optional)"
              placeholder={formatINRExact(m.default_amount)}
              value={overrides[m.id] ?? ""}
              onChange={(e) => setOverrides((o) => ({ ...o, [m.id]: e.target.value }))}
              disabled={saving}
            />
          )}
        </td>
        <td>
          {attached || !isOneOff ? (
            <span className="t-muted">—</span>
          ) : (
            <input
              type="date"
              className="input"
              style={{ width: 150 }}
              title="Due date for this one-off bill"
              value={dueValueFor(m)}
              onChange={(e) => setDueDates((d) => ({ ...d, [m.id]: e.target.value }))}
              disabled={saving}
            />
          )}
        </td>
        <td>
          {attached ? (
            <span className="badge badge-success">Attached</span>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => attach(m)} disabled={saving}>
              Attach
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Attach payment template"
      subtitle="Future bills are generated automatically per the template's recurrence."
      wide
    >
      {masters.loading ? (
        <LoadingBlock rows={5} />
      ) : allRows.length === 0 ? (
        <EmptyState
          icon="dollar-sign"
          title="No payment templates"
          hint="Create one from the Payment Templates page first."
        />
      ) : (
        <div className="col gap-md">
          <div className="row gap-sm" style={{ alignItems: "center" }}>
            <select
              className="select"
              style={{ width: 180 }}
              value={patternFilter}
              onChange={(e) => setPatternFilter(e.target.value)}
            >
              <option value="">All recurrences</option>
              {patternOptions.map((p) => (
                <option key={p} value={p}>{PATTERN_LABEL[p]}</option>
              ))}
            </select>
            <div style={{ position: "relative", flex: 1 }}>
              <Icon
                name="search"
                size={13}
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
              />
              <input
                className="input"
                style={{ paddingLeft: 30 }}
                placeholder="Search templates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {grouped.length === 0 ? (
            <EmptyState icon="search" title="No templates match" hint="Try a different search or recurrence." />
          ) : (
            grouped.map((grp) => (
              <div key={grp.key}>
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div className="t-primary fw-600">{grp.name}</div>
                  <span className="text-xs t-muted">{grp.items.length} template{grp.items.length === 1 ? "" : "s"}</span>
                </div>
                <table className="table">
                  <thead><tr><th>Template</th><th>Amount override</th><th>Due date</th><th></th></tr></thead>
                  <tbody>{grp.items.map((m) => renderRow(m))}</tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}
    </Drawer>
  );
}

function OverrideDrawer({ row, businessId, onClose, onSaved }) {
  const open = !!row;
  const [override, setOverride] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) { setOverride(""); return; }
    setOverride(row.amount_override !== null && row.amount_override !== undefined ? String(row.amount_override) : "");
  }, [row]);

  const submit = async () => {
    setSaving(true);
    try {
      await updateBusinessPaymentMaster(businessId, row.id, {
        amount_override: override === "" ? null : Number(override),
      });
      showToast("success", "Override saved");
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Amount override · ${row?.name || ""}`}
      subtitle={`Default: ${formatINRExact(row?.default_amount)}`}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Override amount (₹)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            className="input"
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder={`Default: ${formatINRExact(row?.default_amount)}`}
          />
          <div className="field-hint">
            Clear the field and save to remove the override (revert to the template default).
          </div>
        </div>
      </div>
    </Drawer>
  );
}
